import { PlayerStatus } from '@tunein/web-player-manager';
import { afterNextPaint } from '@tunein/web-utils';
import classNames from 'clsx';
import flow from 'lodash/flow';
import PropTypes from 'prop-types';
import { Component, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { player } from 'src/client/player';
import { stores } from 'src/client/stores';
import { CIRCLE } from 'src/common/constants/tile';
import connectWithPlayer from 'src/common/decorators/connectWithPlayer';
import { withReactHooks } from 'src/common/decorators/withReactHooks';
import { useStoreSelector } from 'src/common/providers';
import { closeNowPlayingDialog } from '../../../actions/dialog';
import { toggleProfileFavorite } from '../../../actions/favorites';
import { MAP_VIEW } from '../../../constants/paths';
import withContentCardTracking from '../../../decorators/containerItems/withContentCardTracking';
import withNowPlayingContext from '../../../decorators/withNowPlayingContext';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import { selectDiscordState, selectIsDiscord } from '../../../selectors/app';
import { behaviors } from '../../../utils/guideItemTypes';
import { DiscordGeoRestrictionsTileAreaTooltip } from '../../discord/DiscordGeoRestrictionsTooltip';
import GuideItemLink from './GuideItemLink';
import LockedTile from './LockedTile';
import PlayTile from './PlayTile';
import css from './play-button.module.scss';
import SelectableTile from './SelectableTile';

const surfaceShapeClassNames = {
  [CIRCLE]: css.circle,
};

function isConnectingToGuideId(guideItem) {
  const { playerStatus, currentGuideItem } = stores.playerStore.getState();

  return (
    playerStatus === PlayerStatus.LOADING &&
    guideItem?.actions?.play?.guideId === currentGuideItem?.playableGuideId
  );
}

export class WithGuideItemActions extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    guideItem: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    syncedPlayerStatus: PropTypes.string.isRequired,
    syncedPlayerAction: PropTypes.func.isRequired,
    isSyncedWithPlayer: PropTypes.bool.isRequired,
    history: PropTypes.object.isRequired,
    isMobile: PropTypes.bool.isRequired,
    nowPlayingContext: PropTypes.object.isRequired,
    canScrub: PropTypes.bool,
    style: PropTypes.object,
    playButtonStyles: PropTypes.object,
    className: PropTypes.string,
    iconFontSize: PropTypes.string,
    tileSize: PropTypes.string,
    tileShape: PropTypes.string,
    getIsNowPlayingOpen: PropTypes.func.isRequired,
    isClickHandlerEnabled: PropTypes.bool,
    isSelected: PropTypes.bool,
    onClick: PropTypes.func,
    disablePreroll: PropTypes.bool,
    trackingRef: PropTypes.object,
    isDiscord: PropTypes.bool.isRequired,
    hasTunedGuideItem: PropTypes.bool,
  };

  static contextType = LocationAndLocalizationContext;

  navigateAndTuneOnPlay = async (to) => {
    const { history, guideItem } = this.props;
    this.tunePromise = player.onClick(guideItem);
    // tune() should only be called when isPlayable() is true for the guideItem
    // this check was done below when passing in handleGuideItemLinkClick() as the onClick prop

    // Navigating renders the next route, so let the browser paint its response to the tap first.
    await afterNextPaint();
    history.push(to);
  };

  async handleClickForDiscord(to) {
    const { getIsNowPlayingOpen, history, actions, guideItem, hasTunedGuideItem } = this.props;
    const isNowPlayingOpen = getIsNowPlayingOpen();
    const { location } = this.context;
    const defaultActionName = guideItem?.actionName;

    await this.tunePromise;

    if (isConnectingToGuideId(guideItem)) {
      return;
    }

    if (location.pathname === MAP_VIEW) {
      this.tunePromise = player.onClick(guideItem);
      return;
    }

    switch (defaultActionName) {
      case behaviors.browse:
      case behaviors.profile:
        if (!hasTunedGuideItem) {
          this.tunePromise = player.onClick(guideItem);
        }

        if (to.pathname === location.pathname && isNowPlayingOpen) {
          actions.closeNowPlayingDialog();
          return;
        }

        history.push(to);
        break;
      case behaviors.play:
        this.tunePromise = player.onClick(guideItem);
        break;
      default:
    }
  }

  handleGuideItemLinkClick = async (to, event) => {
    event.preventDefault();

    const {
      syncedPlayerAction,
      isSyncedWithPlayer,
      canScrub,
      guideItem,
      getIsNowPlayingOpen,
      nowPlayingContext,
      disableNavigation,
      onClick,
      disablePreroll,
      isDiscord,
    } = this.props;
    const isNowPlayingOpen = getIsNowPlayingOpen();

    onClick?.();

    if (isDiscord) {
      this.handleClickForDiscord(to);
      return;
    }

    if (isSyncedWithPlayer && stores.playerStore.getState().metadata?.isAdBreak) {
      return;
    }

    // when in nowplaying and currently playing item is clicked
    // we want to only scroll and NOT control playback or retune
    if (isSyncedWithPlayer && isNowPlayingOpen) {
      nowPlayingContext.scrollToTop();
      return;
    }

    await this.tunePromise;

    if (isConnectingToGuideId(guideItem)) {
      return;
    }

    if (isSyncedWithPlayer) {
      syncedPlayerAction(null, canScrub);
      return;
    }

    if (!disableNavigation && to) {
      this.navigateAndTuneOnPlay(to);
    } else {
      this.tunePromise = player.onClick(guideItem, {
        skipPreroll: disablePreroll,
      });
    }

    if (isNowPlayingOpen) {
      nowPlayingContext.scrollToTop();
    }
  };

  renderContent() {
    const {
      guideItem,
      syncedPlayerStatus,
      children,
      canScrub,
      style,
      className,
      isMobile,
      tileSize,
      tileShape,
      isClickHandlerEnabled,
      isSelected,
      isDiscord,
      hasTunedGuideItem,
      discordState,
    } = this.props;

    const surfaceClassName = classNames(css.surface, surfaceShapeClassNames[tileShape], className);

    /* No anchor */
    if (!guideItem.actionName) {
      return (
        <div className={classNames(surfaceClassName, css.noAnchor)} style={style}>
          {children}
        </div>
      );
    }

    /* Premium Gated Station - it should show the upsell dialog */
    if (guideItem.isLocked) {
      return (
        <LockedTile
          guideItem={guideItem}
          className={surfaceClassName}
          style={style}
          isClickHandlerEnabled={isClickHandlerEnabled}
        >
          {children}
        </LockedTile>
      );
    }

    /* If it's playable then anchor so tune begins on click */
    if (guideItem.isPlayable) {
      return (
        <GuideItemLink
          dataTestId="guideItemLink"
          onClick={this.handleGuideItemLinkClick}
          guideItem={guideItem}
          className={surfaceClassName}
          style={style}
        >
          <PlayTile
            syncedPlayerStatus={syncedPlayerStatus}
            canScrub={canScrub}
            isMobile={isMobile}
            isDiscord={isDiscord}
            tileSize={tileSize}
            tileShape={tileShape}
            guideId={guideItem?.guideId}
            hasTunedGuideItem={hasTunedGuideItem}
            canControlPlayback={discordState.canControlPlayback}
          />
          {isDiscord && <DiscordGeoRestrictionsTileAreaTooltip guideItem={guideItem} />}
          {children}
        </GuideItemLink>
      );
    }

    if (guideItem.isSelectable) {
      return (
        <SelectableTile
          className={classNames(surfaceClassName, css.selectable)}
          style={style}
          isSelected={isSelected}
        >
          {children}
        </SelectableTile>
      );
    }

    /* Default anchor to browse or profile */
    return (
      <GuideItemLink
        dataTestId="guideItemLink"
        guideItem={guideItem}
        style={style}
        className={surfaceClassName}
      >
        {children}
      </GuideItemLink>
    );
  }

  render() {
    const { playButtonStyles, trackingRef } = this.props;
    return (
      <div
        className={css.playButton}
        style={{ ...playButtonStyles }}
        data-testid="guideItemPlayButtonContainer"
        ref={trackingRef}
      >
        {this.renderContent()}
      </div>
    );
  }
}

export default flow(
  connectWithPlayer,
  withReactHooks(() => {
    const dispatch = useDispatch();
    const actions = useMemo(
      () => bindActionCreators({ toggleProfileFavorite, closeNowPlayingDialog }, dispatch),
      [dispatch],
    );
    const isDiscord = useSelector(selectIsDiscord);
    const discordState = useSelector(selectDiscordState);
    // Only Discord hides tile controls once something is tuned. Elsewhere this stays false, so the
    // first play of a session doesn't re-render every tile on the page.
    const hasTunedGuideItem = useStoreSelector(
      'playerStore',
      (state) => isDiscord && !!state.currentGuideItem?.playableGuideId,
    );

    // Not withRouter: its context changes on every navigation, which would re-render every tile.
    return { actions, discordState, isDiscord, hasTunedGuideItem, history: useHistory() };
  }),
  withNowPlayingContext,
  withContentCardTracking,
)(WithGuideItemActions);
